import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Edit3, Trash2, Loader2, Save, X, Filter, Sparkles, DollarSign } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { SERVICE_CATEGORIES, getServiceCategoryLabel, getServiceCategoryColor } from './LeadCRM';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const currency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n || 0));

const emptyForm = {
  industry: 'kitchens_baths',
  job_type: '',
  name: '',
  description: '',
  materials_cost: 0,
  labor_cost: 0,
  overhead_percent: 15,
  profit_percent: 20,
  sales_tax_percent: 7,
  notes: '',
};

const RatesManager = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);

  const token = () => localStorage.getItem('rooftrack_token');
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const loadCards = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/rate-cards`, authHeaders());
      setCards(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load rate cards');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCards(); }, []);

  // Grouping by industry for display
  const grouped = useMemo(() => {
    const filtered = filterIndustry === 'all'
      ? cards
      : cards.filter(c => c.industry === filterIndustry);
    return SERVICE_CATEGORIES.reduce((acc, cat) => {
      const items = filtered.filter(c => c.industry === cat.id);
      if (items.length > 0) acc[cat.id] = items;
      return acc;
    }, {});
  }, [cards, filterIndustry]);

  // Live preview totals on the form
  const formTotals = useMemo(() => {
    const mat = Number(form.materials_cost) || 0;
    const lab = Number(form.labor_cost) || 0;
    const oh = Number(form.overhead_percent) || 0;
    const pr = Number(form.profit_percent) || 0;
    const tx = Number(form.sales_tax_percent) || 0;
    const subtotal = mat + lab;
    const overhead = subtotal * oh / 100;
    const profitBase = subtotal + overhead;
    const profit = profitBase * pr / 100;
    const taxable = profitBase + profit;
    const tax = taxable * tx / 100;
    const total = taxable + tax;
    return { subtotal, overhead, profit, tax, total };
  }, [form.materials_cost, form.labor_cost, form.overhead_percent, form.profit_percent, form.sales_tax_percent]);

  const openCreate = () => {
    setForm({ ...emptyForm, industry: filterIndustry === 'all' ? 'kitchens_baths' : filterIndustry });
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (card) => {
    setForm({
      industry: card.industry,
      job_type: card.job_type || '',
      name: card.name || '',
      description: card.description || '',
      materials_cost: card.materials_cost || 0,
      labor_cost: card.labor_cost || 0,
      overhead_percent: card.overhead_percent || 0,
      profit_percent: card.profit_percent || 0,
      sales_tax_percent: card.sales_tax_percent || 0,
      notes: card.notes || '',
    });
    setEditingId(card.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.industry || !form.job_type) {
      toast.error('Industry and Job Type are required');
      return;
    }
    setIsSaving(true);
    try {
      if (editingId) {
        const res = await axios.put(`${BACKEND_URL}/api/rate-cards/${editingId}`, form, authHeaders());
        setCards(cards.map(c => c.id === editingId ? res.data : c));
        toast.success('Rate card updated');
      } else {
        const res = await axios.post(`${BACKEND_URL}/api/rate-cards`, form, authHeaders());
        setCards([...cards, res.data]);
        toast.success('Rate card created');
      }
      setShowForm(false);
      setForm(emptyForm);
      setEditingId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rate card');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id, jobType) => {
    if (!window.confirm(`Delete rate card "${jobType}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/rate-cards/${id}`, authHeaders());
      setCards(cards.filter(c => c.id !== id));
      toast.success('Rate card deleted');
    } catch {
      toast.error('Failed to delete rate card');
    }
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" /> Loading rate cards...
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="rates-manager">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <DollarSign className="w-7 h-7 mr-2 text-orange-500" />
            Your Rate Cards
          </h1>
          <p className="text-gray-600 mt-1">Build your own pricing library — one card per job type, per industry.</p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-gradient-to-r from-blue-600 to-orange-500 hover:from-blue-700 hover:to-orange-600 text-white"
          data-testid="new-rate-card-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> New Rate Card
        </Button>
      </div>

      {/* Industry filter */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <Filter className="w-4 h-4 text-gray-500" />
          <Label className="text-sm text-gray-600 mr-2">Filter by industry:</Label>
          <select
            value={filterIndustry}
            onChange={(e) => setFilterIndustry(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            data-testid="rate-industry-filter"
          >
            <option value="all">All industries ({cards.length})</option>
            {SERVICE_CATEGORIES.map(cat => {
              const count = cards.filter(c => c.industry === cat.id).length;
              return (
                <option key={cat.id} value={cat.id}>
                  {cat.icon} {cat.label} ({count})
                </option>
              );
            })}
          </select>
          <div className="ml-auto text-xs text-gray-500 flex items-center">
            <Sparkles className="w-3.5 h-3.5 mr-1 text-amber-500" />
            Starter templates are pre-seeded — edit or duplicate them for your business.
          </div>
        </div>
      </div>

      {/* Empty state */}
      {Object.keys(grouped).length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <p className="text-gray-500 mb-4">
            {filterIndustry === 'all'
              ? 'No rate cards yet. Create your first one to get started.'
              : 'No rate cards in this industry yet.'}
          </p>
          <Button onClick={openCreate} variant="outline" data-testid="empty-new-rate-card-btn">
            <Plus className="w-4 h-4 mr-2" /> New Rate Card
          </Button>
        </div>
      )}

      {/* Grouped list */}
      {Object.entries(grouped).map(([industryId, items]) => {
        const cat = SERVICE_CATEGORIES.find(c => c.id === industryId);
        return (
          <div key={industryId} className="mb-8" data-testid={`industry-section-${industryId}`}>
            <div className="flex items-center mb-3">
              <span className={`inline-block text-sm font-semibold px-3 py-1 rounded-full ${cat?.color || 'bg-gray-100 text-gray-700'}`}>
                {cat?.icon} {cat?.label}
              </span>
              <span className="ml-3 text-sm text-gray-500">{items.length} rate card{items.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map(card => (
                <div
                  key={card.id}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 hover:shadow-md transition-all"
                  data-testid={`rate-card-${card.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 leading-tight pr-2">
                      {card.name || card.job_type}
                    </h3>
                    {card.is_template && (
                      <span className="inline-flex items-center text-xs bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded">
                        <Sparkles className="w-3 h-3 mr-0.5" /> Template
                      </span>
                    )}
                  </div>
                  {card.job_type && card.name && (
                    <p className="text-xs text-gray-500 mb-2">{card.job_type}</p>
                  )}
                  {card.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{card.description}</p>
                  )}

                  <div className="text-sm space-y-1 bg-gray-50 rounded-lg p-3 mb-3">
                    <div className="flex justify-between"><span className="text-gray-600">Materials</span><span className="font-medium">{currency(card.materials_cost)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Labor</span><span className="font-medium">{currency(card.labor_cost)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Overhead ({card.overhead_percent}%)</span><span className="font-medium">{currency(card.overhead_amount)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Profit ({card.profit_percent}%)</span><span className="font-medium">{currency(card.profit_amount)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Sales Tax ({card.sales_tax_percent}%)</span><span className="font-medium">{currency(card.tax_amount)}</span></div>
                    <div className="flex justify-between pt-2 border-t border-gray-200">
                      <span className="font-semibold text-gray-900">Total Price</span>
                      <span className="font-bold text-blue-600 text-lg">{currency(card.total_price)}</span>
                    </div>
                  </div>

                  <div className="flex gap-2 justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEdit(card)}
                      data-testid={`edit-rate-card-${card.id}`}
                    >
                      <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      onClick={() => handleDelete(card.id, card.job_type)}
                      data-testid={`delete-rate-card-${card.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Create/Edit modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8" data-testid="rate-card-form">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingId ? 'Edit Rate Card' : 'New Rate Card'}
              </h2>
              <button
                onClick={() => { setShowForm(false); setForm(emptyForm); setEditingId(null); }}
                className="text-gray-500 hover:text-gray-900"
                data-testid="close-rate-form-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Industry *</Label>
                  <select
                    required
                    value={form.industry}
                    onChange={(e) => setForm({ ...form, industry: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 mt-1"
                    data-testid="rate-form-industry"
                  >
                    {SERVICE_CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.icon} {cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Job Type *</Label>
                  <Input
                    value={form.job_type}
                    onChange={(e) => setForm({ ...form, job_type: e.target.value })}
                    placeholder="e.g., Full Kitchen Remodel"
                    className="mt-1"
                    data-testid="rate-form-job-type"
                  />
                </div>
              </div>

              <div>
                <Label>Rate Card Name (optional)</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Kitchen Remodel — Standard"
                  className="mt-1"
                  data-testid="rate-form-name"
                />
              </div>

              <div>
                <Label>Description</Label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What's included in this job type..."
                  className="w-full border rounded-md px-3 py-2 mt-1 h-16"
                  data-testid="rate-form-description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Materials Cost ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.materials_cost}
                    onChange={(e) => setForm({ ...form, materials_cost: Number(e.target.value) || 0 })}
                    className="mt-1"
                    data-testid="rate-form-materials"
                  />
                </div>
                <div>
                  <Label>Labor Cost ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.labor_cost}
                    onChange={(e) => setForm({ ...form, labor_cost: Number(e.target.value) || 0 })}
                    className="mt-1"
                    data-testid="rate-form-labor"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Overhead (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={form.overhead_percent}
                    onChange={(e) => setForm({ ...form, overhead_percent: Number(e.target.value) || 0 })}
                    className="mt-1"
                    data-testid="rate-form-overhead"
                  />
                </div>
                <div>
                  <Label>Profit (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={form.profit_percent}
                    onChange={(e) => setForm({ ...form, profit_percent: Number(e.target.value) || 0 })}
                    className="mt-1"
                    data-testid="rate-form-profit"
                  />
                </div>
                <div>
                  <Label>Sales Tax (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={form.sales_tax_percent}
                    onChange={(e) => setForm({ ...form, sales_tax_percent: Number(e.target.value) || 0 })}
                    className="mt-1"
                    data-testid="rate-form-tax"
                  />
                </div>
              </div>

              <div>
                <Label>Internal Notes (optional)</Label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Anything the team should know..."
                  className="w-full border rounded-md px-3 py-2 mt-1 h-14"
                  data-testid="rate-form-notes"
                />
              </div>

              {/* Live preview */}
              <div className="bg-gradient-to-br from-blue-600 to-orange-500 text-white rounded-xl p-4" data-testid="rate-form-preview">
                <div className="text-xs uppercase tracking-wide opacity-80 mb-2">Live Preview</div>
                <div className="grid grid-cols-2 gap-y-1 text-sm">
                  <div>Subtotal (Mat + Labor)</div><div className="text-right font-semibold">{currency(formTotals.subtotal)}</div>
                  <div>+ Overhead ({form.overhead_percent}%)</div><div className="text-right font-semibold">{currency(formTotals.overhead)}</div>
                  <div>+ Profit ({form.profit_percent}%)</div><div className="text-right font-semibold">{currency(formTotals.profit)}</div>
                  <div>+ Sales Tax ({form.sales_tax_percent}%)</div><div className="text-right font-semibold">{currency(formTotals.tax)}</div>
                </div>
                <div className="border-t border-white/30 mt-2 pt-2 flex justify-between items-center">
                  <span className="text-base font-semibold opacity-90">Total Price</span>
                  <span className="text-2xl font-extrabold" data-testid="rate-form-preview-total">{currency(formTotals.total)}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={() => { setShowForm(false); setForm(emptyForm); setEditingId(null); }}
                data-testid="rate-form-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="rate-form-save-btn"
              >
                {isSaving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" /> {editingId ? 'Update' : 'Create'} Rate Card</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RatesManager;
